/**
 * Schmaler Zugang zum Backend.
 *
 * Der Access-Token lebt im Speicher, nicht im localStorage, das schützt vor
 * XSS-Diebstahl. Die Sitzung überlebt trotzdem einen Neuladen, weil das
 * Refresh-Token in einem httpOnly-Cookie liegt und beim Start einmal
 * eingelöst wird.
 */

/**
 * Alles läuft über denselben Ursprung: In der Entwicklung reicht der
 * Vite-Proxy `/api` an Port 4000 durch, produktiv liegen Oberfläche und API
 * ohnehin auf derselben Domain. Gebraucht wird das für Verweise, die der
 * Browser selbst ansteuert statt fetch -- etwa der Google-Knopf, der eine
 * echte Seitennavigation auslöst.
 */
export const apiBase = '/api';

let accessToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function setToken(token: string | null): void {
  accessToken = token;
}
export function getToken(): string | null {
  return accessToken;
}
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code = 'error',
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
  /** Für die typische „Credits reichen nicht"-Behandlung im UI */
  get isPaymentRequired(): boolean {
    return this.status === 402;
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** true = 401 nicht automatisch als Abmeldung behandeln */
  quiet?: boolean;
}

let refreshing: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  if (!refreshing) {
    refreshing = fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) return false;
        const data = (await res.json()) as { token: string };
        accessToken = data.token;
        return true;
      })
      .catch(() => false)
      .finally(() => { refreshing = null; });
  }
  return refreshing;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, quiet, ...init } = options;

  const send = async (): Promise<Response> =>
    fetch(path.startsWith('/api') ? path : `/api${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        ...(body !== undefined && !(body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...(init.headers ?? {}),
      },
      ...(body !== undefined
        ? { body: body instanceof FormData ? body : JSON.stringify(body) }
        : {}),
    });

  let res = await send();

  // Einmal stillschweigend erneuern, bevor wir den Nutzer rauswerfen
  if (res.status === 401 && !quiet && (await refreshSession())) {
    res = await send();
  }

  if (res.status === 401 && !quiet) {
    onUnauthorized?.();
  }

  if (!res.ok) {
    let message = `Fehler ${res.status}`;
    let code = 'error';
    let details: unknown;
    try {
      const data = (await res.json()) as { error?: string; code?: string; details?: unknown };
      message = data.error ?? message;
      code = data.code ?? code;
      details = data.details;
    } catch { /* keine JSON-Antwort */ }
    throw new ApiError(res.status, message, code, details);
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  /** DELETE darf einen Körper tragen, Löschungen, die eine Bestätigung
   *  mitschicken müssen, brauchen das. */
  del: <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', body }),
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', body: form }),
  refresh: refreshSession,
};

/**
 * Live-Kanal. EventSource kann keine Header setzen, deshalb wandert der
 * Token in die Adresse, er ist kurzlebig.
 *
 * Läuft absichtlich NICHT über den Cloudflare-Weiterleiter: Eine
 * Dauerverbindung durch dessen Worker hindurch bricht dort nach einer Weile
 * mit 524 ab. Die Anfrage geht deshalb direkt an VITE_API_ORIGIN (Render),
 * cross-origin — geht hier, weil die Auth über den Token in der Adresse
 * läuft statt über Cookies, und CORS_ORIGINS beim Server genau diese
 * Website-Adresse erlaubt. Ohne VITE_API_ORIGIN (lokale Entwicklung) bleibt
 * es beim relativen Pfad über den Vite-Dev-Proxy.
 */
const apiOrigin = (import.meta.env.VITE_API_ORIGIN as string | undefined)?.replace(/\/$/, '') ?? '';

export function openEventStream(
  onEvent: (event: Record<string, unknown>) => void,
  projectId?: string | null,
): () => void {
  if (!accessToken) return () => {};

  /*
   * Das Token wird bei JEDEM Verbindungsaufbau frisch gelesen. Es lebt nur
   * eine halbe Stunde: Mit dem Token vom Seitenaufruf scheiterte jedes
   * Wiederverbinden danach still, und alle Ansichten froren auf „Rendert"
   * oder „Wird gepostet" ein, bis jemand die Seite neu lud.
   */
  const url = () => {
    const params = new URLSearchParams({ token: accessToken ?? '' });
    if (projectId) params.set('projectId', projectId);
    return `${apiOrigin}/api/events?${params.toString()}`;
  };

  let source: EventSource | null = new EventSource(url());
  let retry: ReturnType<typeof setTimeout> | null = null;
  let closed = false;

  const attach = (es: EventSource) => {
    es.onmessage = (e) => {
      try { onEvent(JSON.parse(e.data)); } catch { /* Herzschlag */ }
    };
    es.onerror = () => {
      es.close();
      if (closed) return;
      // Nach einem Verbindungsabbruch mit etwas Abstand neu verbinden
      retry = setTimeout(() => {
        // Erst die Sitzung erneuern: Ist das Token abgelaufen, hilft sonst kein neuer Versuch.
        void refreshSession().catch(() => false).then(() => {
          if (closed || !accessToken) return;
          source = new EventSource(url());
          attach(source);
          // In der Lücke kann etwas passiert sein, das niemand gemeldet hat.
          onEvent({ type: 'stream.reconnected' });
        });
      }, 3000);
    };
  };

  attach(source);

  return () => {
    closed = true;
    if (retry) clearTimeout(retry);
    source?.close();
  };
}
